import React, { useCallback, useContext, useEffect, useReducer, useState } from 'react';
import LanguageReducer from 'services/reducers/langaugeReducer';
import coursesReducercer, { coursesInitState } from 'services/reducers/coursesReducer.js';
import userReducer, { userInitState } from 'services/reducers/userReducer';
import ApiClass from 'services/api';
import { LANGUAGES, ROLES } from 'constants/index';

let _debounceTimeOut;

export const AppContext = React.createContext();

const Provider = ({ children }) => {
	const englishObj = LANGUAGES.find(language => language.iso === 'EN');

	const [lang, dispatchLanguage] = useReducer(LanguageReducer, englishObj.translation_id);
	const [user, dispatchUser] = useReducer(userReducer, userInitState);
	const [courses, dispatchCourses] = useReducer(coursesReducercer, coursesInitState);
	const [isLoading, setIsLoading] = useState(false);
	const [video, setVideo] = useState(null);
	const [ddls, setDdls] = useState({});
	const [generalTranslations, setGeneralTranslations] = useState({});
	const [notifications, setNotifications] = useState([]);

	// NOTIFICATION MODAL STATE
	const [responseMessage, setResponseMessage] = useState({ visible: false, message: '', failed: false });

	const Api = new ApiClass(setIsLoading);
	useEffect(() => {
		const language = LANGUAGES.find(_language => _language.id === lang);
		if (!language) return;
		const dir = language.isRtl ? 'rtl' : 'ltr';
		document.body.setAttribute('dir', dir);
	}, [lang]);

	//gets all managements DDLs ONLY for Management ROLES
	async function GetAllDDLs() {
		try {
			const _ddls = await Api.GetAllManagementDDLS();
			if (_ddls) setDdls(_ddls);
		} catch (error) {
			console.error('Failed to fetch DDLs', error);
			setDdls({});
		}
	}

	//GET DDLS ON LOAD - ONLY for Management ROLES
	useEffect(() => {
		if (user?.roleId !== ROLES.Student && user?.isMgmtLogin) {
			GetAllDDLs();
		}
	}, [user.roleId]);

	//Get all app translations.
	useEffect(() => {
		async function fetchGeneralTranslations() {
			try {
				const _generalTranslations = await Api.GetTranslations();
				if (_generalTranslations) setGeneralTranslations(_generalTranslations);
			} catch (ex) {
				console.error('Failed to fetch translations', ex);
			}
		}
		fetchGeneralTranslations();
	}, []);

	// Add a new notification
	const addNotification = useCallback((message, type = 'success', duration = 4000) => {
		const id = Date.now(); // Create a unique ID for the notification

		// Add the new notification to the state
		setNotifications(prev => [...prev, { id, message, type, duration }]);

		// Automatically remove the notification after its duration
		setTimeout(() => {
			setNotifications(prev => prev.filter(notif => notif.id !== id));
		}, duration);
	}, []);

	//popups management
	const showResponseMessagePopup = (message, failed = false, debounceTimeInMS = 5000) => {
		clearTimeout(_debounceTimeOut);

		setResponseMessage({
			visible: true,
			message,
			failed,
		});

		_debounceTimeOut = setTimeout(() => setResponseMessage({ visible: false, message: '', failed: false }), debounceTimeInMS);
	};

	const state = {
		lang,
		setLang: dispatchLanguage,
		courses,
		dispatchCourses,
		user,
		dispatchUser,
		Api,
		isLoading,
		setIsLoading,
		video,
		setVideo,
		showResponseMessagePopup,
		responseMessage,
		setResponseMessage,
		addNotification,
		notifications,
		ddls,
		setDdls,
		generalTranslations,
	};

	return <AppContext.Provider value={state}>{children}</AppContext.Provider>;
};

export const useAppContext = () => {
	return useContext(AppContext);
};

export default Provider;
